import React, { useState } from 'react';
import { Phone } from 'lucide-react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardUrl } from '../../utils/dashboardUrl';
import loginImage from '../../assets/images/services-1.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import 'react-phone-input-2/lib/style.css';
import '../common/phone-input-overrides.css';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onForgotPassword }) => {
  const { login } = useAuth();

  const [authType, setAuthType] = useState('email'); // 'email' or 'phone'
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const toE164 = (value) => (value ? `+${value}` : '');

  const isValidInternationalPhone = (value) => {
    if (!value) return false;
    const parsed = parsePhoneNumberFromString(toE164(value));
    return !!parsed && parsed.isValid();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (authType === 'email') {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else {
      if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!isValidInternationalPhone(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const loginIdentifier = authType === 'email' ? formData.email : toE164(formData.phone);
      const result = await login(loginIdentifier, formData.password, authType);

      if (result.success) {
        onClose();
        setTimeout(() => {
          let redirect = 'https://app.tecnvi-ai.com/dashboard/overview';
          try {
            redirect = getDashboardUrl();
          } catch (err) {
            // fall back to hard-coded URL above
          }
          window.location.href = redirect;
        }, 150);
      } else {
        const newErrors = {};

        if (result.code === 'EMAIL_NOT_VERIFIED' || result.code === 'PHONE_NOT_VERIFIED') {
          newErrors.general = 'Please verify your ' + authType + ' before logging in.';
          newErrors.action = 'verify_' + authType;
        } else if (result.code === 'ACCOUNT_LOCKED') {
          newErrors.general = 'Your account has been temporarily locked due to multiple failed login attempts. Please try again later.';
        } else if (result.code === 'ACCOUNT_NOT_ACTIVATED') {
          newErrors.general = 'Your account is not activated. Please verify your ' + authType + ' first.';
        } else {
          newErrors.general = result.error || 'Login failed. Invalid email/phone or password.';
        }

        setErrors(newErrors);
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmail = () => {
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div
        className="login-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${loginImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div className="modal-form-section">
          <div className="form-content" data-lenis-prevent>
            <div className="auth-modal-header text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div className="modal-logo" style={{ marginBottom: '0', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <img
                  src={tecnvironsLogo}
                  alt="Tecnvirons"
                  className="modal-logo-image"
                  style={{
                    width: '60px',
                    maxWidth: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    backgroundColor: 'transparent',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              </div>

              <h2 style={{ marginTop: '12px', marginBottom: '14px', fontSize: '22px', fontWeight: '600', whiteSpace: 'nowrap', display: 'block', width: '100%', textAlign: 'center', color: 'black' }}>Welcome Back</h2>
            </div>

            <div className="form-main">
              <p className="form-subtitle text-center">Sign in to your Business Unit account</p>

              {errors.general && (
                <div className="error-message">
                  {errors.general}
                  {errors.action === 'verify_email' && (
                    <button
                      type="button"
                      className="verify-email-btn"
                      onClick={handleVerifyEmail}
                    >
                      Verify Email Now
                    </button>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="login-form">
                <div className="auth-type-toggle" style={{
                  display: 'flex',
                  gap: '10px',
                  marginBottom: '20px',
                  borderBottom: '2px solid #e0e0e0',
                  paddingBottom: '10px'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthType('email');
                      setFormData(prev => ({ ...prev, phone: '' }));
                      setErrors(prev => ({ ...prev, phone: '' }));
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      background: authType === 'email' ? '#7c3aed' : '#f3f4f6',
                      color: authType === 'email' ? 'white' : '#6b7280',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthType('phone');
                      setFormData(prev => ({ ...prev, email: '' }));
                      setErrors(prev => ({ ...prev, email: '' }));
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: 'none',
                      background: authType === 'phone' ? '#7c3aed' : '#f3f4f6',
                      color: authType === 'phone' ? 'white' : '#6b7280',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Phone width={16} height={16} style={{ display: 'inline', marginRight: '5px' }} />
                    Phone
                  </button>
                </div>

                {authType === 'email' ? (
                  <div className="modal-form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder=" "
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="email">Email</label>
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                ) : (
                  <div className="modal-form-group">
                    <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', color: '#4b5563', fontSize: '14px' }}>Phone Number</label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, phone: value }));
                        if (errors.phone) {
                          setErrors(prev => ({ ...prev, phone: '' }));
                        }
                      }}
                      country="in"
                      countryCodeEditable={true}
                      disableDropdown={false}
                      enableSearch={true}
                      preferredCountries={['in', 'us', 'gb', 'ae', 'sa', 'pk', 'bd']}
                      searchPlaceholder="Search country..."
                      searchNotFound="No country found"
                      disabled={isLoading}
                    />
                    {errors.phone && <span className="field-error">{errors.phone}</span>}
                  </div>
                )}

                <div className="modal-form-group">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder=" "
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      required
                    />
                    <label htmlFor="password">Password</label>
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                <div className="form-options">
                  <div className="login-remember-me-container">
                    <input
                      type="checkbox"
                      id="rememberMeLogin"
                      name="rememberMe"
                      className="auth-custom-checkbox"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <label htmlFor="rememberMeLogin" className="login-remember-me-label">Remember me</label>
                  </div>
                  <button
                    type="button"
                    className="forgot-password-btn"
                    onClick={() => {
                      onForgotPassword && onForgotPassword();
                    }}
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={loginImage} alt="Tecnvirons Login" className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>

            <div className="overlay-content">
              <div className="overlay-card">
                <h3>Welcome Back,<br />Your Way!</h3>
                <p>Continue your journey with unified ERP and CRM workflows built for your business.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Access Your Dashboard,<br />Beyond Boundaries!</h3>
                <p>Resume managing orders, dispatch, and leads in one place.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
